import React, { useState, useEffect } from "react"
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  styled,
  Typography,
  Backdrop,
  Tooltip,
  Collapse,
  TextField
} from "@mui/material"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import EditIcon from "@mui/icons-material/Edit"
import { useDispatch, useSelector } from "react-redux"
import { useTheme } from "@mui/system"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import RewardForm from "./components/RewardForm"
import RedemptionsListModal from "./components/RedemptionsListModal"
import SpreeQuantityModal from "./components/SpreeQuantityModal"
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog"
import { Formik } from "formik"
import { format } from "date-fns"
import { validationSchemaReward } from "helpers/validationSchema"
import {
  createRewardAction,
  deleteRewardAction,
  getCardTypesAction,
  getCategoriesAction,
  getRewardsAction,
  getTiersAction,
  updateRewardAction,
  uploadGiftCardCouponsAction,
  reorderRewardsAction
} from "redux/superAdmin/actions"
import { rewardManagementColumns } from "helpers/constant"
import ProfileDisplayer from "components/ProfileDisplayer"
import TableNoRecords from "components/TableNoRecords"
import { superAdminAction } from "redux/superAdmin/slice/user"

const StatusText = ({ active }) => (
  <Typography
    variant="caption3"
    sx={{
      color: active ? "#4CAF50" : "#9E9E9E",
      fontFamily: "Inter-Regular",
      fontWeight: 600
    }}>
    {active ? "Active" : "Inactive"}
  </Typography>
)

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: "12px 24px",
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
}))

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "16px 24px 16px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": {
      paddingRight: 0
    }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid #272a2c`,
    "&:last-child": {
      borderBottom: "none"
    }
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "sticky",
    borderBottom: `2px solid #272a2c`,
    "&:last-child": {
      paddingRight: 0
    }
  }
}))

const ActionsMenu = ({ onEdit, onRedemptions, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const openMenu = Boolean(anchorEl)
  const theme = useTheme()

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ color: "text.secondary" }}>
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Backdrop
        sx={{ color: theme.palette.white.main, zIndex: 1300 }}
        open={openMenu}
        onClick={handleMenuClose}>
        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
          PaperProps={{
            sx: {
              marginTop: "15px",
              backgroundColor: theme.palette.background.default,
              borderRadius: "12px",
              "& .MuiList-root": {
                backgroundColor: theme.palette.background.default,
                width: "225px",
                boxSizing: "border-box",
                padding: "10px 0"
              }
            }
          }}>
          <StyledMenuItem
            onClick={() => {
              handleMenuClose()
              onEdit()
            }}>
            <Typography variant="body1Regular">Edit</Typography>
          </StyledMenuItem>
          <StyledMenuItem
            onClick={() => {
              handleMenuClose()
              if (onRedemptions) onRedemptions()
            }}>
            <Typography variant="body1Regular">Redemptions</Typography>
          </StyledMenuItem>
          <StyledMenuItem
            onClick={() => {
              handleMenuClose()
              onDelete()
            }}>
            <Typography sx={{ color: theme.palette.red.main }} variant="body1Regular">
              Delete
            </Typography>
          </StyledMenuItem>
        </Menu>
      </Backdrop>
    </>
  )
}

const RewardManagement = ({ addTrigger = 0 }) => {
  const theme = useTheme()
  const { userRestaurant } = useSelector((state) => state.user)
  const restaurantId = userRestaurant?.id
  const {
    tiers,
    categories,
    cardTypes,
    uploadedImageId,
    rewards: rewardsState,
    rewardsSpreeActive
  } = useSelector((state) => state.superAdmin)
  const rewards = Array.isArray(rewardsState) ? rewardsState : rewardsState?.results || []
  const dispatch = useDispatch()
  const [formOpen, setFormOpen] = useState(false)
  const [selectedReward, setSelectedReward] = useState(null)
  const [isEdit, setIsEdit] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [rewardToDelete, setRewardToDelete] = useState(null)
  const [redemptionsModalOpen, setRedemptionsModalOpen] = useState(false)
  const [selectedRewardForRedemptions, setSelectedRewardForRedemptions] = useState(null)
  const [spreeQtyModalOpen, setSpreeQtyModalOpen] = useState(false)
  const [spreeQtyReward, setSpreeQtyReward] = useState(null)
  const { isLoading } = useSelector((state) => state.app)
  const [expandedTiers, setExpandedTiers] = useState({})
  const [search, setSearch] = useState("")

  useEffect(() => {
    if (!restaurantId) return
    const timer = setTimeout(() => {
      dispatch(getRewardsAction({ restaurantId, search }))
    }, 500)
    return () => clearTimeout(timer)
  }, [search, restaurantId])

  useEffect(() => {
    if (!restaurantId) return
    dispatch(getTiersAction({ restaurantId }))
    dispatch(getCategoriesAction())
    dispatch(getCardTypesAction())
  }, [restaurantId])

  useEffect(() => {
    if (addTrigger > 0) {
      dispatch(superAdminAction.setUploadedImageId(""))
      setSelectedReward(null)
      setIsEdit(false)
      setFormOpen(true)
    }
  }, [addTrigger])

  const [initialFormValues, setInitialFormValues] = useState({
    name: "",
    description: "",
    tier: "",
    category: "",
    coin_cost: "",
    card_type: "",
    inventory_limited: false,
    total_redemption_limit: null,
    is_enabled: true,
    poweredBy: false,
    valid_from: new Date(),
    valid_until: null,
    per_user_redemption_limit: 1,
    image: null,
    image_id: null,
    coupon_codes: [],
    details: ""
  })

  useEffect(() => {
    if (!formOpen) return

    if (isEdit && selectedReward) {
      setInitialFormValues({
        name: selectedReward?.name || "",
        description: selectedReward?.description || "",
        tier:
          (typeof selectedReward?.tier === "object"
            ? selectedReward?.tier?.id
            : selectedReward?.tier) || "",
        category:
          (typeof selectedReward?.category === "object"
            ? selectedReward?.category?.id
            : selectedReward?.category) || "",
        coin_cost: selectedReward?.coin_cost || "",
        card_type:
          (typeof selectedReward?.card_type === "object"
            ? selectedReward?.card_type?.id
            : selectedReward?.card_type_id || selectedReward?.card_type) || "",
        inventory_limited:
          selectedReward?.total_redemption_limit !== null &&
          selectedReward?.total_redemption_limit !== undefined,
        // Per-spree model: show the quantity set for the active spree (0 if unset),
        // not the legacy lifetime total_redemption_limit. null = unlimited card.
        total_redemption_limit:
          selectedReward?.total_redemption_limit === null ||
          selectedReward?.total_redemption_limit === undefined
            ? null
            : selectedReward?.spree_total_quantity || 0,
        is_enabled: selectedReward?.status === "ACTIVE",
        poweredBy: selectedReward?.show_powered_by || false,
        valid_from: selectedReward?.valid_from ? new Date(selectedReward?.valid_from) : null,
        valid_until: selectedReward?.valid_until ? new Date(selectedReward?.valid_until) : null,
        per_user_redemption_limit: selectedReward?.per_user_redemption_limit || 1,
        image: selectedReward?.image
          ? { url: selectedReward?.image_url || null, id: selectedReward?.image || null }
          : null,
        coupon_codes: selectedReward?.coupon_codes || [],
        details: selectedReward?.terms_and_conditions || ""
      })
    } else {
      setInitialFormValues({
        name: "",
        description: "",
        tier: "",
        category: "",
        coin_cost: "",
        card_type: "",
        inventory_limited: false,
        total_redemption_limit: null,
        is_enabled: true,
        poweredBy: false,
        valid_from: new Date(),
        valid_until: null,
        per_user_redemption_limit: 1,
        image: null,
        image_id: null,
        coupon_codes: [],
        details: ""
      })
    }
  }, [formOpen, isEdit, selectedReward])

  const handleEditReward = (reward) => {
    dispatch(superAdminAction.setUploadedImageId(""))
    setSelectedReward(reward)
    setIsEdit(true)
    setFormOpen(true)
  }

  const handleFormClose = () => {
    dispatch(superAdminAction.setUploadedImageId(""))
    setFormOpen(false)
    setSelectedReward(null)
    setIsEdit(false)
  }

  const handleSubmit = (formData) => {
    console.log("formData", formData)
    const payload = {
      ...formData,
      restaurants: [restaurantId],
      status: formData.is_enabled ? "ACTIVE" : "INACTIVE",
      total_redemption_limit: formData.inventory_limited
        ? parseInt(formData.total_redemption_limit, 10)
        : null,
      per_user_redemption_limit: parseInt(formData.per_user_redemption_limit) || 1,
      valid_from: formData.valid_from ? format(formData.valid_from, "yyyy-MM-dd") : null,
      valid_until: formData.valid_until ? format(formData.valid_until, "yyyy-MM-dd") : null,
      image: uploadedImageId ? uploadedImageId : formData?.image?.id || null,
      terms_and_conditions: formData.details,
      show_powered_by: !!formData.poweredBy
    }
    delete payload.details
    delete payload.poweredBy
    const couponCodes = formData.coupon_codes || []

    const action = isEdit ? updateRewardAction : createRewardAction
    const params = isEdit
      ? {
          rewardId: selectedReward.id,
          payload,
          callback: (resData) => onSaveSuccess(resData, couponCodes)
        }
      : { payload, callback: (resData) => onSaveSuccess(resData, couponCodes) }

    dispatch(action(params))
  }

  const onSaveSuccess = async (resData, couponCodes) => {
    const rewardId = selectedReward?.id || resData?.id
    if (rewardId && couponCodes.length > 0) {
      dispatch(uploadGiftCardCouponsAction({ rewardId, codes: couponCodes }))
    }
    handleFormClose()
  }

  const handleDeleteClick = (reward) => {
    setRewardToDelete(reward)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = () => {
    dispatch(
      deleteRewardAction({
        rewardId: rewardToDelete.id,
        callback: () => {
          setDeleteDialogOpen(false)
          setRewardToDelete(null)
        }
      })
    )
  }

  const handleToggleTier = (tierId) => {
    setExpandedTiers((prev) => ({
      ...prev,
      [tierId]: !prev[tierId]
    }))
  }

  const onDragEnd = (result) => {
    const { source, destination } = result

    if (!destination) return

    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return
    }

    const tierId = source.droppableId
    const tier = rewards.find((t) => t.id?.toString() === tierId)

    if (!tier) return

    const updatedRewards = [...tier.rewards]
    const [removed] = updatedRewards.splice(source.index, 1)
    updatedRewards.splice(destination.index, 0, removed)

    const payload = updatedRewards.map((item, idx) => ({
      id: item.id,
      order: idx + 1
    }))

    dispatch(reorderRewardsAction({ payload }))
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}>
        <TextField
          size="small"
          placeholder="Search rewards..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            width: "300px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "rgba(255, 255, 255, 0.05)"
            }
          }}
        />
      </Box>
      <Box
        sx={{
          mb: 2,
          px: 2,
          py: 1.25,
          borderRadius: "12px",
          border: "1px solid",
          borderColor: rewardsSpreeActive ? "rgba(170, 142, 112, 0.5)" : "rgba(255,255,255,0.12)",
          backgroundColor: "rgba(255, 255, 255, 0.04)"
        }}>
        <Typography variant="body2Regular" color="text.secondary">
          {rewardsSpreeActive
            ? "A shopping spree is active. The quantities below apply to the current spree. Quantities reset at the start of every spree, so set each reward's total quantity for this spree."
            : "No active shopping spree. Total/Available/Redeemed reset to 0 for the next spree; you can set quantities once a spree is active."}
        </Typography>
      </Box>
      {rewards?.length ? (
        <DragDropContext onDragEnd={onDragEnd}>
          {rewards?.map((tier) => {
            const isExpanded = expandedTiers[tier.id]

            return (
              <Box
                key={tier.id}
                sx={{
                  mb: 3,
                  border: `1px solid #272a2c`,
                  borderRadius: "12px",
                  overflow: "hidden"
                }}>
                <Box
                  onClick={() => handleToggleTier(tier.id)}
                  sx={{
                    p: "20px 24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    backgroundColor: theme.palette.background.main,
                    borderBottom: isExpanded ? `1px solid #272a2c` : "none",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.05)"
                    },
                    transition: "background-color 0.2s ease"
                  }}>
                  <Typography
                    variant="h4"
                    sx={{ color: "secondary.main", textTransform: "uppercase" }}>
                    {tier.name}
                  </Typography>
                  <IconButton sx={{ color: theme.palette.text.primary }}>
                    {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>
                </Box>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <TableContainer sx={{ borderRadius: 0 }}>
                    <CustomTable stickyHeader>
                      <TableHead>
                        <TableRow>
                          {rewardManagementColumns?.map((column) => (
                            <TableCell key={column.id} align={column.align}>
                              <Typography variant="body2Regular" style={{ opacity: 0.6 }}>
                                {column.label}
                              </Typography>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <Droppable droppableId={tier.id.toString()}>
                        {(provided) => (
                          <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                            {tier.rewards?.length ? (
                              tier.rewards.map((reward, idx) => (
                                <Draggable
                                  key={reward.id}
                                  draggableId={reward.id?.toString()}
                                  index={idx}>
                                  {(provided, snapshot) => (
                                    <TableRow
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      sx={{
                                        display: snapshot.isDragging ? "table" : undefined,
                                        backgroundColor: snapshot.isDragging
                                          ? "rgba(255, 255, 255, 0.05)"
                                          : theme.palette.background.main,
                                        ...(reward.status !== "ACTIVE"
                                          ? { opacity: "0.6" }
                                          : { opacity: "1" }),
                                        zIndex: snapshot.isDragging ? 9999 : "auto"
                                      }}>
                                      <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                          <Tooltip title="Drag to reorder">
                                            <DragIndicatorIcon
                                              sx={{
                                                color: "text.secondary",
                                                opacity: 0.5,
                                                cursor: "grab"
                                              }}
                                            />
                                          </Tooltip>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Box display="flex" alignItems="center" gap={1.5}>
                                          <ProfileDisplayer image={reward?.image_url} />
                                          <Typography variant="body2Regular">
                                            {reward?.name || "-"}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2Regular">
                                          {tier?.name || "-"}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <StatusText active={reward?.status === "ACTIVE"} />
                                      </TableCell>
                                      <TableCell>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                          <Typography variant="body2Regular">
                                            {reward?.total_redemption_limit === null
                                              ? "∞"
                                              : reward?.spree_total_quantity || 0}
                                          </Typography>
                                          {reward?.total_redemption_limit !== null && (
                                            <Tooltip
                                              title={
                                                rewardsSpreeActive
                                                  ? "Set quantity for this spree"
                                                  : "Quantity can be set once a spree is active"
                                              }>
                                              <span>
                                                <IconButton
                                                  size="small"
                                                  disabled={!rewardsSpreeActive}
                                                  onClick={() => {
                                                    setSpreeQtyReward(reward)
                                                    setSpreeQtyModalOpen(true)
                                                  }}>
                                                  <EditIcon fontSize="inherit" />
                                                </IconButton>
                                              </span>
                                            </Tooltip>
                                          )}
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2Regular">
                                          {reward?.total_redemption_limit === null
                                            ? "∞"
                                            : Math.max(
                                                0,
                                                (reward?.spree_total_quantity || 0) -
                                                  (reward?.spree_redeemed || 0)
                                              )}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2Regular">
                                          {reward?.spree_redeemed || 0}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2Regular">
                                          {reward?.pending_redemption || "-"}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2Regular">
                                          {reward?.rejected_redemption || "-"}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <ActionsMenu
                                          onEdit={() => handleEditReward(reward)}
                                          onRedemptions={() => {
                                            setSelectedRewardForRedemptions(reward)
                                            setRedemptionsModalOpen(true)
                                          }}
                                          onDelete={() => handleDeleteClick(reward)}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </Draggable>
                              ))
                            ) : (
                              <TableNoRecords
                                colSpan={rewardManagementColumns.length}
                                label="No rewards configured for this tier."
                              />
                            )}
                            {provided.placeholder}
                          </TableBody>
                        )}
                      </Droppable>
                    </CustomTable>
                  </TableContainer>
                </Collapse>
              </Box>
            )
          })}
        </DragDropContext>
      ) : (
        <Box
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <Typography variant="body2Regular" textAlign="center">
            {isLoading
              ? "Please Wait we are getting the rewards..."
              : "No rewards configured yet. Use 'Add New Reward' to create one."}
          </Typography>
        </Box>
      )}

      {formOpen && (
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialFormValues}
          validationSchema={validationSchemaReward}
          enableReinitialize={true}>
          {(formik) => (
            <RewardForm
              open={formOpen}
              onClose={handleFormClose}
              formik={formik}
              reward={selectedReward}
              isEdit={isEdit}
              tiers={tiers}
              categories={categories}
              cardTypes={cardTypes}
              loading={isLoading}
            />
          )}
        </Formik>
      )}

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false)
          setRewardToDelete(null)
        }}
        onDelete={handleConfirmDelete}
        title="Delete Reward"
        description={`Are you sure you want to delete "${
          rewardToDelete?.name || rewardToDelete?.title
        }"? `}
        btnText="Delete"
      />

      {/* Redemptions Modal */}
      {redemptionsModalOpen && (
        <RedemptionsListModal
          open={redemptionsModalOpen}
          onClose={() => {
            setRedemptionsModalOpen(false)
            setSelectedRewardForRedemptions(null)
          }}
          giftCardId={selectedRewardForRedemptions?.id}
          rewardName={selectedRewardForRedemptions?.name}
        />
      )}

      {/* Set Spree Quantity Modal */}
      {spreeQtyModalOpen && (
        <SpreeQuantityModal
          open={spreeQtyModalOpen}
          onClose={() => {
            setSpreeQtyModalOpen(false)
            setSpreeQtyReward(null)
          }}
          reward={spreeQtyReward}
          restaurantId={restaurantId}
          search={search}
        />
      )}
    </Box>
  )
}

export default RewardManagement
